"use client";

/**
 * Imperative store + click guard for the payments-paused pop-up.
 *
 * Mirrors purchase-confirmation-store.ts: a module-level store read through
 * `useSyncExternalStore`, rendered by the root-mounted <PaymentsPausedHost />.
 * A store rather than a window event so the rich intent object survives intact
 * and only one pop-up can ever be open.
 *
 * The export that matters at call sites is `interceptIfPaymentsPaused()`. Every
 * pay handler in the app starts with it:
 *
 *     if (interceptIfPaymentsPaused({ kind: "upgrade", surface: "…", … })) return;
 *
 * That single line has to sit BEFORE the Stripe call but AFTER the existing
 * click analytics, so a blocked purchase still counts as intent in every funnel
 * we already track — the whole point of intercepting on click instead of
 * disabling the button.
 */
import { useSyncExternalStore } from "react";

import {
  isPaymentsPaused,
  type PausedPurchaseIntent,
} from "@/lib/billing/payments-paused";
import { trackPaymentsPausedBlocked } from "@/lib/analytics";

/** The active intent plus a monotonic id, so a re-show re-mounts the modal. */
export type PausedPurchaseRequest = PausedPurchaseIntent & { id: number };

let current: PausedPurchaseRequest | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/** Open the pop-up for a blocked purchase. Prefer `interceptIfPaymentsPaused`. */
export function showPaymentsPaused(intent: PausedPurchaseIntent) {
  current = { ...intent, id: nextId++ };
  emit();
}

/** Close the pop-up (CTA, close button, backdrop, or Escape). */
export function dismissPaymentsPaused() {
  if (current === null) return;
  current = null;
  emit();
}

/**
 * The one-line guard every checkout handler opens with.
 *
 * Returns `true` when payments are paused — the caller must then return without
 * touching Stripe. Returns `false` in the normal case, so with the flag unset
 * this is a constant-false branch and every existing flow is untouched.
 *
 * Recording the intent happens here rather than in the modal so it fires even if
 * the buyer closes the pop-up without emailing: the analytics row is the record
 * of the lost sale, and it must not depend on them doing anything.
 */
export function interceptIfPaymentsPaused(
  intent: PausedPurchaseIntent,
): boolean {
  if (!isPaymentsPaused()) return false;
  trackPaymentsPausedBlocked({
    intent: intent.kind,
    surface: intent.surface,
    plan: intent.plan ?? null,
    cadence: intent.cadence ?? null,
    credits: intent.credits ?? null,
    amountUsd: intent.amountUsd ?? null,
    label: intent.label ?? null,
  });
  showPaymentsPaused(intent);
  return true;
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): PausedPurchaseRequest | null {
  return current;
}

function getServerSnapshot(): PausedPurchaseRequest | null {
  return null;
}

/** Subscribe a component to the active blocked purchase (null when none). */
export function usePaymentsPaused(): PausedPurchaseRequest | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
